import { Component, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { StorageService } from 'src/app/services/storage.service';
import * as Constants from "../../utils/Constants";
import { WindowEnergyRatingDetailsComponent } from '../window-energy-rating-details/window-energy-rating-details.component';
import { ImageModalComponent } from '../image-modal/image-modal.component';
import { EnergyRatingUKApiService } from 'src/app/services/energy-rating-uk-api.service';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { WindowPayloadBuilderService } from 'src/app/services/window-payload-builder.service';
import { environment } from 'src/environments/environment';
import { setDefaultDimensions } from 'src/app/utils/calculation';
import { ThermalApiService } from 'src/app/services/thermal-api.service';
import { RatingAgreementModalComponent } from '../rating-agreement-modal/rating-agreement-modal.component';

@Component({
  selector: 'app-window-energy-ratings',
  templateUrl: './window-energy-ratings.component.html',
  styleUrls: ['./window-energy-ratings.component.scss'],
  standalone: false,
})
export class WindowEnergyRatingsComponent implements OnInit {
  storedProject: any;
  selectedCountry: string = "";
  selectedRatingWindowType: string = "1";
  selectedFrWindowType: string = "13";
  selClimateZone = "3";
  countryData: any[] = Constants.energyRatingsCountries;
  climateZonesData: any[] = Constants.climateZones;
  agWithoutGasket: any = 0;
  airleakage: any = 0.1;
  ool: any = 4.19;
  result: any = {};

  isFormValid = false;
  isLoading = false;
  hasError = false;
  errorMessage = '';

  otherWindowTypes = Constants.data_windows.filter(w => [1, 9].includes(w.id));
  frWindowTypes = Constants.data_windows.filter(w => [13, 14].includes(w.id));
  // Track previous values for comparison
  private previousValues = {
    selectedCountry: null as string | null,
    selClimateZone: null as string | null,
    agWithoutGasket: null as number | null,
    airleakage: null as number | null,
    ool: null as number | null,
    selectedRatingWindowType: null as string | null,
    selectedFrWindowType: null as string | null
  };
  // Create a Subject for debouncing
  private ercalculationTrigger$ = new Subject<{ selectedCountry: string, selClimateZone: string, agWithoutGasket: number, airleakage: number, ool: number, selectedRatingWindowType: string, selectedFrWindowType: string }>();
  private ercalculationSubscription: Subscription;
  private agreementAcceptedFor: string | null = null;
  constructor(private modalController: ModalController, private storageService: StorageService,
    private energyRatingUkService: EnergyRatingUKApiService, private windowPayloadBuilder: WindowPayloadBuilderService, private thermalApi: ThermalApiService
  ) {
    console.log(this.otherWindowTypes)
    console.log(this.selectedRatingWindowType)

    // Subscribe to debounced calculation trigger
    this.ercalculationSubscription = this.ercalculationTrigger$
      .pipe(
        debounceTime(500), // Wait 500ms after last change
        distinctUntilChanged((prev, curr) =>
          prev.selectedCountry === curr.selectedCountry &&
          prev.selClimateZone === curr.selClimateZone &&
          prev.agWithoutGasket === curr.agWithoutGasket &&
          prev.airleakage === curr.airleakage &&
          prev.ool === curr.ool &&
          prev.selectedRatingWindowType === curr.selectedRatingWindowType &&
          prev.selectedFrWindowType === curr.selectedFrWindowType
        )
        // distinctUntilChanged() // Only trigger if values changed
      )
      .subscribe(() => {
        this.performCalculations();
      });
  }

  async ngOnInit() { }

  async ionViewWillEnter() {
    await this.init();
    this.agreementAcceptedFor = null;
    if (this.selectedCountry) {
      const ok = await this.showAgreementIfNeeded(this.selectedCountry);
      if (!ok) {
        this.selectedCountry = '';
        this.result = null;
      }
    }
  }

  private async init(): Promise<void> {
    try {
      const res = await this.storageService.getProject();
      this.storedProject = res ? JSON.parse(res) : null;
      // Initial calculation
      await this.loadCalculatorData();

    } catch (error) {
      console.error('Error initializing spacer data:', error);
    }
  }

  private async loadCalculatorData(): Promise<void> {
    try {
      const res = await this.storageService.getProject();
      const storedProject = res ? JSON.parse(res) : null;
      const saved = storedProject?.windowEnergyRatingResult;
      if (saved) {
        this.selectedCountry = saved.country ?? '';

        // restore country-specific input params
        if (saved?.ukResult) {
          this.agWithoutGasket = saved.ukResult.agWithoutGasket ?? null;
          this.airleakage = saved.ukResult.airleakage ?? 0.1;
          this.ool = saved.ukResult.ool ?? 4.19;
        }
        if (saved?.pheResult) {
          this.selClimateZone = saved.pheResult.climateZone ?? '3';
          this.selectedRatingWindowType = saved.pheResult.selectedRatingWindowType ?? '1';
          this.selectedFrWindowType = saved.pheResult.selectedFrWindowType ?? '13';
        }
      } else {
        // defaults for first open
        this.selectedCountry = '';
        // this.selClimateZone = '3';
        // this.selectedRatingWindowType = "1";
        // this.selectedFrWindowType = "13";
      }
      this.validateForm();

      if (this.selectedCountry) {
        await this.performCalculations();
      } else {
        this.result = null;
      }
    } catch (error) {
      console.error('Error loading calculator data:', error);
    }
  }


  ngOnDestroy() {
    // Clean up subscription
    if (this.ercalculationSubscription) {
      this.ercalculationSubscription.unsubscribe();
    }
  }

  async next() {
    if (!this.isFormValid || !this.result) return;

    let windowEnergyRatingResult: any = {
      country: this.selectedCountry,
      chResult: null,
      dkResult: null,
      frResult: null,
      ukResult: null,
      pheResult: null
    };

    switch (this.selectedCountry) {
      case 'Schweiz': {
        const chCustomData = {
          dimensions: { dimA: 1.55, dimB: 1.15, dimC: 0 },
          windowtype: { id: 2 }
        };
        const { ag, af, aw } = await this.calcChAreas();
        // Get gValue from stored project
        const gValue = this.storedProject?.windowglas?.gValue ?? 0;
        const hs = ag * (gValue / 100) * 2.0;
        const uw = await new Promise<number>((resolve, reject) => {
          this.thermalApi.calculateUw(chCustomData).subscribe({
            next: (res) => resolve(res.value),
            error: (err) => reject(err)
          });
        });
        const hw = uw * aw;

        windowEnergyRatingResult.chResult = {
          uweq: this.result.uweq ?? 0,
          rating: this.result.rating ?? '',
          a: 1.55,
          b: 1.15,
          ag,
          af,
          aw,
          uw,
          hw,
          hs
        };
        break;
      }
      case 'Dänemark':
        windowEnergyRatingResult.dkResult = {
          gg: this.result.gg ?? 0,
          gw: this.result.gw ?? 0,
          eref: this.result.eref ?? 0,
          werclass: this.result.werClass ?? '',
          areaWindow: this.result.areaWindow ?? 0,
          areaGlass: this.result.areaGlass ?? 0,
          uw: this.result.uw ?? 0
        };
        break;

      case 'Frankreich':
        windowEnergyRatingResult.frResult = {
          selectedFrWindowType: this.result.selectedFrWindowType ?? '13',
          badge_z1: this.result.badgeZ1 ?? '',
          value_z1: this.result.valueZ1 ?? 0,
          badge_z2: this.result.badgeZ2 ?? '',
          value_z2: this.result.valueZ2 ?? 0,
          badge_z3: this.result.badgeZ3 ?? '',
          value_z3: this.result.valueZ3 ?? 0,

          badge_confort_z1: this.result.badgeConfortZ1 ?? '',
          value_confort_z1: this.result.valueConfortZ1 ?? 0,
          badge_confort_z2: this.result.badgeConfortZ2 ?? '',
          value_confort_z2: this.result.valueConfortZ2 ?? 0,
          badge_confort_z3: this.result.badgeConfortZ3 ?? '',
          value_confort_z3: this.result.valueConfortZ3 ?? 0,

          tlw: this.result.tLw ?? 0,
          sw: this.result.sw ?? 0
        };
        break;

      case 'Großbritannien':
        windowEnergyRatingResult.ukResult = {
          rat: this.result.rat ?? '',
          wer: this.result.wer ?? 0,
          gw: this.result.gw ?? 0,
          lfac: this.result.lfac ?? 0,
          // ── input params for restore on load ──
          agWithoutGasket: this.agWithoutGasket ?? 0.1,
          airleakage: this.airleakage ?? 0.1,
          ool: this.ool ?? 4.19
        };
        break;

      case 'Other':
        windowEnergyRatingResult.pheResult = {
          selectedRatingWindowType: this.result.selectedRatingWindowType ?? '1',
          psig: this.result.psig ?? '',
          ug: this.result.ug ?? 0,
          psiOpaque: this.result.psiOpaque ?? 0,
          uwPhi: this.result.uwPhi ?? 0,
          effClass: this.result.effClass ?? '',
          // ── input param for restore on load ──
          climateZone: this.selClimateZone ?? '3'
        };
        break;
    }

    try {
      await this.storageService.updateProject({ windowEnergyRatingResult });
      console.log('Window energy rating saved:', windowEnergyRatingResult);
    } catch (error) {
      console.error('Error saving rating result:', error);
    }

    this.modalController.dismiss(null, 'confirm');
  }


  async clickRequirements() {
    const modal = await this.modalController.create({
      component: ImageModalComponent,
      cssClass: 'configuration-modal',
      componentProps: {
        "image_path": "assets/images/phe/phe_requirements.png"
      }
    });
    await modal.present();
  }

  async clickSeeDetails() {
    const modal = await this.modalController.create({
      component: WindowEnergyRatingDetailsComponent,
      cssClass: 'configuration-modal',
      componentProps: {
        "selCountry": this.selectedCountry,
        "result": this.result
      }
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();
  }

  async onCountryChange() {
    this.validateForm();
    this.agreementAcceptedFor = null;
    const ok = await this.showAgreementIfNeeded(this.selectedCountry);
    if (!ok) {
      this.selectedCountry = '';
      return;
    }
  }
  onWindowChange() {
    this.validateForm();
  }
  onClimateZoneChange() {
    this.validateForm();
  }

  validateAirLeakage(event: any) {
    let value = parseFloat(event.target.value);

    if (isNaN(value)) {
      this.airleakage = 0;
      return;
    }

    if (value < 0) {
      this.airleakage = 0;
    } else if (value > 2) {
      this.airleakage = 2;
    } else {
      this.airleakage = value;
    }
    this.validateForm();
    console.log(this.airleakage)
  }
  validateOpeningLight(event: any) {
    const value = parseFloat(event.target.value);

    if (isNaN(value)) {
      this.ool = 0;
      return;
    }
    if (value > 5) {
      event.target.value = this.ool;
    } else {
      this.ool = value;
    }
    this.validateForm();
  }
  validateGasket(event: any) {
    const value = parseFloat(event.target.value);

    if (isNaN(value)) {
      this.agWithoutGasket = 0;
      return;
    }
    this.agWithoutGasket = value;
    this.validateForm();
  }

  async performCalculations() {
    console.log("call API")
    // Only call API if form is valid
    // if (!this.isFormValid) {
    //   return;
    // }

    try {
      // Call your API here
      const result = await this.callCalculationApi();

      // Update UI with results
      this.updateResults(result);
    } catch (error) {
      console.error('Error performing calculations:', error);
    }
  }
  private calcWerGlassArea(gasket: number, project: any): number {
    const wf = project.windowframe;

    const wValues = wf.wValues ?? [];

    const fj = wValues[0] ?? 0;
    const fh = wValues[1] ?? 0;
    const fs = wValues[2] ?? 0;
    const sj = wValues[3] ?? 0;
    const sh = wValues[4] ?? 0;
    const ss = wValues[5] ?? 0;
    const m = wValues[6] ?? 0;

    const a = project.dimensions.a;
    const b = project.dimensions.b;

    gasket = gasket / 1000;

    const lw = (a / 2) - (m / 2) - fj - (2 * gasket);
    const lh = b - fh - fs - (2 * gasket);
    const rw = (a / 2) - (m / 2) - sj - (2 * gasket);
    const rh = b - sh - ss - (2 * gasket);

    return (lw * lh) + (rw * rh);
  }

  private async callCalculationApi(): Promise<any> {
    let apiParam = {};
    let endpointUrl = "";
    if (this.selectedCountry == 'Schweiz') {
      endpointUrl = environment.caluwin_api.endpoints.energy_rating_ch;
    } else if (this.selectedCountry == 'Dänemark') {
      endpointUrl = environment.caluwin_api.endpoints.energy_rating_dk;
    } else if (this.selectedCountry == 'Frankreich') {
      endpointUrl = environment.caluwin_api.endpoints.energy_rating_fr;
    } else if (this.selectedCountry == 'Großbritannien') {
      endpointUrl = environment.caluwin_api.endpoints.energy_rating_uk;
    } else if (this.selectedCountry == 'Other') {
      endpointUrl = environment.caluwin_api.endpoints.energy_rating_phe;
    }

    if (this.selectedCountry == 'Schweiz') {
      const customData = {
        "dimensions": { "dimA": 1.55, "dimB": 1.15, "dimC": 0 },
        "windowtype": { "id": 2 }
      }
      apiParam = (await this.windowPayloadBuilder.buildWindowPayloads(customData))?.window1;
    } else if (this.selectedCountry == 'Dänemark') {
      apiParam = (await this.windowPayloadBuilder.buildWindowPayloads())?.window1;
    } else if (this.selectedCountry == 'Großbritannien') {
      const windowType = 3;
      const dimensions: any = setDefaultDimensions(windowType)
      const customData = {
        "dimensions": dimensions,
        "windowtype": { "id": windowType }
      }
      apiParam = {
        "windowConfiguration": (await this.windowPayloadBuilder.buildWindowPayloads(customData))?.window1,
        "agWithoutGasket": this.calcWerGlassArea(this.agWithoutGasket, this.storedProject),
        "airleakage": this.airleakage,
        "ool": this.ool
      };
    } else if (this.selectedCountry == 'Frankreich') {
      const dimensions: any = setDefaultDimensions(Number(this.selectedFrWindowType))
      const customData = {
        "dimensions": dimensions,
        "windowtype": { "id": this.selectedFrWindowType }
      }
      apiParam = {
        "windowConfiguration": (await this.windowPayloadBuilder.buildWindowPayloads(customData))?.window1,
        "af": 0.4
      };
    } else if (this.selectedCountry == 'Other') {
      const dimensions: any = setDefaultDimensions(Number(this.selectedRatingWindowType))
      const customData = {
        "dimensions": dimensions,
        "windowtype": { "id": this.selectedRatingWindowType }
      }
      apiParam = {
        "windowConfiguration": (await this.windowPayloadBuilder.buildWindowPayloads(customData))?.window1,
        "climateZone": this.selClimateZone
      };
    }

    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';

    try {
      const response: any = await this.energyRatingUkService.calculateEnergyRating(apiParam, endpointUrl).toPromise();

      if (response) {
        console.log(response)
        this.result = response;
      } else {
        throw new Error(response.message || 'Calculation failed');
      }

    } catch (error: any) {
      console.error('Calculation error:', error);
      this.hasError = true;
      this.errorMessage = error.message || 'Failed to calculate comfort values. Please try again.';

    } finally {
      this.isLoading = false;
    }
  }

  private updateResults(result: any) {
    console.log("result", result)
  }


  validateForm(): void {
    console.log("validate form called")
    // First validate and reset values to ensure they're within limits
    // this.validateAndResetValues();

    // Check if required fields have values
    const hasAirLeakage = this.airleakage != null && this.airleakage != undefined && !isNaN(this.airleakage);
    const hasOol = this.ool != null && this.ool != undefined && !isNaN(this.ool);
    // const hasEPhsi = this.ePhi != null && this.ePhi != undefined && !isNaN(this.ePhi) && this.ePhi > 0;

    this.isFormValid = hasAirLeakage && hasOol;

    // Only emit if values have changed
    if (
      this.previousValues.selectedCountry !== this.selectedCountry ||
      this.previousValues.selClimateZone !== this.selClimateZone ||
      this.previousValues.agWithoutGasket !== this.agWithoutGasket ||
      this.previousValues.airleakage !== this.airleakage ||
      this.previousValues.ool !== this.ool ||
      this.previousValues.selectedRatingWindowType !== this.selectedRatingWindowType ||
      this.previousValues.selectedFrWindowType !== this.selectedFrWindowType
    ) {
      console.log("calculationTrigger next")
      this.ercalculationTrigger$.next({
        selectedCountry: this.selectedCountry,
        selClimateZone: this.selClimateZone,
        agWithoutGasket: this.agWithoutGasket,
        airleakage: this.airleakage,
        ool: this.ool,
        selectedRatingWindowType: this.selectedRatingWindowType,
        selectedFrWindowType: this.selectedFrWindowType
      });

      // Update previous values
      this.previousValues = {
        selectedCountry: this.selectedCountry,
        selClimateZone: this.selClimateZone,
        agWithoutGasket: this.agWithoutGasket,
        airleakage: this.airleakage,
        ool: this.ool,
        selectedRatingWindowType: this.selectedRatingWindowType,
        selectedFrWindowType: this.selectedFrWindowType
      };
    }
  }
  close() {
    this.modalController.dismiss();
  }

  private async calcChAreas(): Promise<{ ag: number; af: number; aw: number }> {
    const customData = {
      dimensions: { dimA: 1.55, dimB: 1.15, dimC: 0 },
      windowtype: { id: 2 }
    };
    const payload = (await this.windowPayloadBuilder.buildWindowPayloads(customData))?.window1;
    return {
      aw: payload.windowArea,
      af: payload.frameArea,
      ag: payload.glassArea
    };
  }

  private getAgreementType(country: string): 'swiss' | 'uk' | 'passive' | null {
    if (country === 'Schweiz') return 'swiss';
    if (country === 'Großbritannien') return 'uk';
    if (country === 'Other') return 'passive';
    return null;
  }

  private async showAgreementIfNeeded(country: string): Promise<boolean> {
    const agreementType = this.getAgreementType(country);
    if (!agreementType) return true;

    if (this.agreementAcceptedFor === agreementType) return true;

    const modal = await this.modalController.create({
      component: RatingAgreementModalComponent,
      cssClass: 'agreement-modal',
      backdropDismiss: false,
      canDismiss: async (_data?: any, role?: string) => role === 'confirm',
      componentProps: { type: agreementType }
    });

    await modal.present();
    const { role } = await modal.onWillDismiss();

    if (role === 'confirm') {
      this.agreementAcceptedFor = agreementType;
      return true;
    }

    return false;
  }
}
